import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk } from "../store";
import { getErrorMessage } from "../helpers/get-error-message";
import { checkIsTablet } from "@helpers/is-tablet";
import { isLiteTmsSupplierRole } from "@helpers/permission";
import {
  resetApi,
  loginApi,
  registerApi,
  getUserApi,
  logoutApi,
  resetConfirmApi,
  refreshAccessTokenApi,
} from "./api";

export type User = {
  id: number;
  ext_customer_id: number;
  inviter_id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  status: string;
  last_login: string;
  created_at: string;
} | null;

interface AuthState {
  user: User;
  loading: boolean;
  error: string | null;
  success: string | null;
}

const initialState: AuthState = {
  user: null,
  loading: false,
  error: null,
  success: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginStart(state: AuthState) {
      state.loading = true;
      state.error = null;
    },
    loginSuccess(state: AuthState) {
      state.loading = false;
      state.error = null;
    },
    commonSuccess(state: AuthState, action: PayloadAction<string>) {
      state.loading = false;
      state.error = null;
      state.success = action.payload;
    },
    loginFailure(state: AuthState, action: PayloadAction<string>) {
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("accessToken");
      state.loading = false;
      state.user = null;
      state.success = null;
      state.error = action.payload;
    },
    logout(state: AuthState) {
      state.user = null;
      state.error = null;
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("accessToken");
    },
    setUser(state: AuthState, action: PayloadAction<User>) {
      state.user = action.payload;
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  setUser,
  commonSuccess,
} = authSlice.actions;

export default authSlice.reducer;

export const login =
  (
    email: string,
    password: string,
    navigate: (path: string) => void,
  ): AppThunk =>
  async (dispatch) => {
    dispatch(loginStart());
    try {
      const { access_token, refresh_token } = await loginApi(email, password);

      localStorage.setItem("refreshToken", refresh_token);
      localStorage.setItem("accessToken", access_token);
      dispatch(loginSuccess());

      const user = await getUserApi();
      dispatch(setUser(user));

      const isTablet = checkIsTablet();

      if (user?.role === "WMS+Operations") {
        navigate("/wms");
      } else if (isLiteTmsSupplierRole(user?.role ?? "")) {
        navigate("/tms");
      } else if (user?.role === "Operations" || user?.role === "Customer") {
        navigate("/shippings");
      } else if (isTablet) {
        navigate("/wms");
      } else {
        navigate("/offers");
      }
    } catch (err) {
      dispatch(loginFailure(getErrorMessage(err)));
    }
  };

export const registration =
  ({
    token,
    password,
    navigate,
  }: {
    token: string | null;
    password: string;
    navigate: any;
  }): AppThunk =>
  async (dispatch) => {
    dispatch(loginStart());
    try {
      await registerApi(token, password);
      dispatch(commonSuccess("Registration complete! You can now log in."));
      navigate("/login", { state: { fromRedirect: true } });
    } catch (err) {
      dispatch(loginFailure(getErrorMessage(err)));
    }
  };

export const resetConfirm =
  ({
    token,
    password,
    navigate,
  }: {
    token: string | null;
    password: string;
    navigate: any;
  }): AppThunk =>
  async (dispatch) => {
    dispatch(loginStart());
    try {
      await resetConfirmApi(token, password);
      dispatch(
        commonSuccess("Password changed! Log in with your new password."),
      );
      navigate("/login", { state: { fromRedirect: true } });
    } catch (err) {
      dispatch(loginFailure(getErrorMessage(err)));
    }
  };

export const getUser = (): AppThunk => async (dispatch) => {
  dispatch(loginStart());
  try {
    const user = await getUserApi();
    dispatch(setUser(user));
    dispatch(loginSuccess());
  } catch (err) {
    const error =
      (err as any)?.response?.data?.detail ||
      "Incorrect credentials. Check your login details and try again";
    dispatch(loginFailure(error));
  }
};

export const refreshAccessToken = (): AppThunk => async (dispatch) => {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) {
    dispatch(logout());
    return;
  }
  dispatch(loginStart());
  try {
    const { access_token, refresh_token } =
      await refreshAccessTokenApi(refreshToken);
    localStorage.setItem("refreshToken", refresh_token);
    localStorage.setItem("accessToken", access_token);
    dispatch(loginSuccess());
  } catch (err) {
    dispatch(loginFailure(getErrorMessage(err)));
  }
};

export const logoutUser =
  (navigate: any): AppThunk =>
  async (dispatch) => {
    try {
      await logoutApi();
      dispatch(logout());
      navigate("/login");
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(loginFailure(error));
    }
  };

export const reset =
  (email: string): AppThunk =>
  async (dispatch) => {
    dispatch(loginStart());
    try {
      await resetApi(email);
      dispatch(commonSuccess("Your message has been sent successfully"));
    } catch (err) {
      dispatch(loginFailure(getErrorMessage(err)));
    }
  };
